import {
  getPickupAppointmentUrl,
  getReturnsUrl,
  getStoresUrl,
} from '../config/api.config'
import { ROLE, type Role } from '../contexts/CustomerHubContext'
import { getResolvedCountryCode } from '../lib/urlCountryCode'

import {
  getActiveOrderCtaKind,
  getActiveOrderProducts,
  getActiveOrderRowType,
  getActiveOrderStep,
  getActiveOrderSubtitle,
  getActiveOrderTimelineVariant,
  getActiveOrderTitleKey,
  shouldShowActiveOrderTimeline,
} from './activeOrders'

import type { ActiveOrder } from '../api/activeOrdersApi'
import type { Environment } from '../App'
import type {
  OrderStateCard,
  OrderStateCta,
  TimelineLabels,
} from '@susu/order-list'

/**
 * Turns raw `active_orders` rows into display text. `@susu/order-list` carries
 * no i18n of its own, so the hub resolves every string before handing cards to
 * the order-details view.
 */

/** Minimal shape of `useI18N().t` — avoids importing the provider here. */
type Translate = (key: string, parameters?: Record<string, string>) => string

/**
 * Timeline step captions. `@susu/order-list` has English fallbacks baked in,
 * so these must be supplied for NL/ZH cards not to render half-translated.
 */
export function toTimelineLabels(t: Translate): TimelineLabels {
  return {
    ordered: t('activeOrders.timeline.ordered'),
    shipped: t('activeOrders.timeline.shipped'),
    delivered: t('activeOrders.timeline.delivered'),
    'being-altered': t('activeOrders.timeline.beingAltered'),
    ready: t('activeOrders.timeline.ready'),
    registered: t('activeOrders.timeline.registered'),
    // The one caption read from the order-list namespace: it is already
    // translated in all 12 locales, where a new activeOrders key would be
    // English everywhere until Crowdin catches up (RP-6203).
    received: t('orderList.timeline.received'),
    exchanged: t('activeOrders.timeline.exchanged'),
  }
}

export interface ActiveOrderText {
  title: string
  subtitle: string | null
}

export function resolveActiveOrderText(
  order: ActiveOrder,
  t: Translate,
  language: string,
  today?: Date,
): ActiveOrderText {
  const titleKey = getActiveOrderTitleKey(order)
  const subtitle = getActiveOrderSubtitle(order, language, today)

  return {
    // `t` echoes the key back when it can't resolve one, so unknown producer
    // statuses fall back to the raw trait string instead.
    title: titleKey ? t(titleKey) : order.fulfillment_status,
    subtitle: subtitle
      ? t(`activeOrders.subtitle.${subtitle.kind}`, subtitle.params)
      : null,
  }
}

/** Host-app locale, for the CTAs that deep-link into another Suitsupply app. */
export interface ActiveOrderCtaLocale {
  /** UI language, e.g. `en`. */
  language: string
  /** Host app country code, e.g. `it`. */
  countryCode: string
  /** Who is looking. Gates the returns link — see below. */
  role?: Role
}

/**
 * Where each CTA goes. Track order follows the carrier link the trait supplies,
 * so it is dropped when the shipment has not been dispatched yet; the QR label
 * is dropped the same way once `return_label_url` stops being populated.
 *
 * Book a pick-up appointment deep-links into the appointment tool for the exact
 * store, which needs `store_code`. Without it we fall back to the store locator
 * rather than dropping the action — the trait only populates `store_code` on
 * alteration and ship-to-store cards, and those are the ones that reach here.
 *
 * The Return CTA is customer-only: a style advisor handles a return at the
 * till, not by sending the shopper to the returns portal, so the row is hidden
 * in store rather than shown and disabled.
 */
export function resolveActiveOrderCta(
  order: ActiveOrder,
  t: Translate,
  env: Environment,
  locale: ActiveOrderCtaLocale,
): OrderStateCta | null {
  const kind = getActiveOrderCtaKind(order)
  if (!kind) return null

  if (kind === 'returnOrExchange' && locale.role === ROLE.STYLE_ADVISOR) {
    return null
  }

  let href: string | null
  switch (kind) {
    case 'trackOrder':
      href = order.order_tracking_link
      break
    case 'returnOrExchange':
      href = getReturnsUrl(env, order.order_id, locale.countryCode)
      break
    case 'bookPickupAppointment': {
      const storeCode = order.store_code?.trim()
      href = storeCode
        ? getPickupAppointmentUrl({
            storeCode,
            storeCountryCode: order.store_country_code,
            language: locale.language,
            countryCode: locale.countryCode,
          })
        : getStoresUrl(env, locale.countryCode)
      break
    }
    case 'showQrCode':
      href = order.return_label_url ?? null
      break
  }

  if (!href) return null

  const downloadName =
    kind === 'showQrCode' ? order.return_label_download_name : null

  return {
    kind,
    label: t(`activeOrders.cta.${kind}`),
    href,
    ...(downloadName && { downloadName }),
  }
}

export function toOrderStateCards(
  orders: ActiveOrder[],
  t: Translate,
  language: string,
  env: Environment,
  today?: Date,
  /** Host app country code; resolved from the URL when the caller omits it. */
  countryCode: string = getResolvedCountryCode(),
  /** Who is looking — hides the returns link for in-store sessions. */
  role?: Role,
): OrderStateCard[] {
  return orders.map((order) => {
    const { title, subtitle } = resolveActiveOrderText(
      order,
      t,
      language,
      today,
    )

    return {
      orderId: order.order_id,
      status: title,
      subtitle,
      products: getActiveOrderProducts(order),
      timelineVariant: getActiveOrderTimelineVariant(order),
      currentStep: getActiveOrderStep(order),
      showTimeline: shouldShowActiveOrderTimeline(order),
      cta: resolveActiveOrderCta(order, t, env, {
        language,
        countryCode,
        role,
      }),
      rowType: getActiveOrderRowType(order),
    }
  })
}
